import { Component, Prop, State, h, Element, Host } from '@stencil/core';

const COMPONENT_NAME = 'stats-counter-card-type-3';

/**
 * Animated counter card element with caption displayed above the counter value.
 * Renders caption first, then the animated counter value, then optional description. Right-aligned by default.
 * Differs from type-1 and type-2 by reversing the caption/value order and default alignment.
 *
 * @category Stats
 * @example
 *   <stats-counter-card-type-3 value="$150" caption="Revenue" description="Per user annually"></stats-counter-card-type-3>
 */
@Component({
  tag: 'stats-counter-card-type-3',
  styleUrl: 'index.scss',
  shadow: false,
})
export class CounterCardType3 {
  @Element() host: HTMLElement;

  /**
   * The value to display in the component.
   * Can be a string or a string with characters (e.g., "50%", "$10M", "100+").
   * @required
   * @maxLength 4
   */
  @Prop() value!: string;
  /**
   * The animation speed in milliseconds.
   * Defines the total duration of the animation.
   * @default 1500
   */
  @Prop() speed: number = 1500;
  /**
   * Horizontal text alignment: 'left', 'center', or 'right'
   * @default 'right'
   */
  @Prop() align: 'left' | 'center' | 'right' = 'right';
  /**
   * Caption or label shown below or above the value (up to 80 characters, will be truncated if exceeds 2 lines)
   * @required
   */
  @Prop() caption!: string;
  /**
   * Optional description for counter (up to 120 characters, will be truncated if exceeds 3 lines).
   * @required
   */
  @Prop() description: string;

  @State() currentValue: string | number = 0;
  private finalNumber: number = 0;
  private prefix: string = '';
  private suffix: string = '';
  private mutationObserver: MutationObserver;

  componentWillLoad() {
    // Guard against undefined value
    if (!this.value) {
      this.currentValue = '';
      return;
    }

    const match = this.value.match(/^([^\d]*)?([\d.,]+)([^\d]*)?$/);
    if (match) {
      this.prefix = match[1] || '';
      this.finalNumber = parseFloat(match[2]);
      this.suffix = match[3] || '';
    } else {
      this.currentValue = this.value;
    }
  }

  componentDidLoad() {
    // Skip MutationObserver setup in SSR environment (MutationObserver is not available in Node.js)
    if (typeof MutationObserver === 'undefined') {
      return;
    }

    this.mutationObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          if (this.host.classList.contains('is-visible')) {
            this.animate();
            this.mutationObserver.disconnect();
          }
        }
      });
    });

    this.mutationObserver.observe(this.host, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  disconnectedCallback() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  animate() {
    if (typeof this.currentValue === 'string') {
      return;
    }

    const duration = this.speed;
    const startTime = performance.now();
    const startValue = 0;
    const endValue = this.finalNumber;

    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    const step = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      let progress = Math.min(elapsed / duration, 1);

      progress = easeOutCubic(progress);

      const current = startValue + (endValue - startValue) * progress;

      this.currentValue = Math.round(current);

      if (elapsed < duration) {
        requestAnimationFrame(step);
      } else {
        this.currentValue = Math.round(endValue);
      }
    };

    requestAnimationFrame(step);
  }

  render() {
    const classes = [COMPONENT_NAME, this.align ? `${COMPONENT_NAME}--${this.align} card-bordered` : ''].filter(Boolean).join(' ');

    // Get position from host attributes if present (for microdata)
    const positionAttr = this.host.getAttribute('position');
    const hasMicrodata = this.host.hasAttribute('itemscope');

    return (
      <Host class={classes}>
        <me-heading class={`${COMPONENT_NAME}__caption`} variant="6" itemProp="name">
          {this.caption}
        </me-heading>
        <div class={`${COMPONENT_NAME}__counter`}>
          {this.prefix}
          {this.currentValue}
          {this.suffix}
        </div>
        {this.description && (
          <me-text class={`${COMPONENT_NAME}__description`} itemProp="description">
            {this.description}
          </me-text>
        )}
        {hasMicrodata && positionAttr && <meta itemProp="position" content={positionAttr} />}
      </Host>
    );
  }
}
